import React, { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Alert,
    CircularProgress,
    MenuItem,
    ToggleButtonGroup,
    ToggleButton,
    Typography,
    Paper,
    Stack,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Tooltip,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    AttachFile as AttachFileIcon,
    Delete as DeleteIcon,
    OpenInNew as OpenInNewIcon,
    Close as CloseIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import ProjectPicker from "../../pickers/ProjectPicker";
import QuotePicker from "../../pickers/QuotePicker";
import ReportService from "../../../services/Report.service";

const CATEGORY_CHOICES = ["Shop", "Field", "Office"];

const EMPTY_FORM = {
    linkedTo: "project",
    project: null,
    quote: null,
    report_date: format(new Date(), "yyyy-MM-dd"),
    category: "",
    witnesses: "",
    participants: "",
    location: "",
    comments: "",
};

const today = format(new Date(), "yyyy-MM-dd");

export default function IncidentReportFormDialog({
    open,
    onClose,
    item,
    initialProject,
    onSaved,
}) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [reportData, setReportData] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const fileInputRef = useRef(null);

    const isEditing = Boolean(item);

    useEffect(() => {
        if (!open) return;
        if (item) {
            setReportData(item);
            setForm({
                linkedTo: item.project_detail ? "project" : "quote",
                project: item.project_detail
                    ? { id: item.project, ...item.project_detail }
                    : null,
                quote: item.quote_detail
                    ? { id: item.quote, ...item.quote_detail }
                    : null,
                report_date: item.report_date || today,
                category: item.category || "",
                witnesses: item.witnesses || "",
                participants: item.participants || "",
                location: item.location || "",
                comments: item.comments || "",
            });
        } else {
            setReportData(null);
            setPendingAttachments((prev) => {
                prev.forEach((a) => {
                    if (a.objectUrl) URL.revokeObjectURL(a.objectUrl);
                });
                return [];
            });
            setForm({
                ...EMPTY_FORM,
                project: initialProject || null,
            });
        }
        setError("");
    }, [open, item, initialProject]);

    const handleLinkedToChange = (_, val) => {
        if (!val) return;
        setForm((f) => ({ ...f, linkedTo: val, project: null, quote: null }));
    };

    const set = (field) => (value) =>
        setForm((f) => ({ ...f, [field]: value }));

    const handleSave = async () => {
        if (!form.report_date) {
            setError("Report date is required.");
            return;
        }
        if (form.report_date > today) {
            setError("Report date cannot be in the future.");
            return;
        }

        const payload = {
            report_date: form.report_date,
            category: form.category || null,
            witnesses: form.witnesses,
            participants: form.participants,
            location: form.location,
            comments: form.comments,
            project: form.linkedTo === "project" ? form.project?.id : null,
            quote: form.linkedTo === "quote" ? form.quote?.id : null,
            ...(!isEditing && pendingAttachments.length > 0
                ? { attachments: pendingAttachments.map((a) => a.id) }
                : {}),
        };

        setLoading(true);
        setError("");
        try {
            if (isEditing) {
                await ReportService.updateIncidentReport(item.id, payload);
            } else {
                await ReportService.createIncidentReport(payload);
            }
            onSaved();
            onClose();
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                JSON.stringify(err?.response?.data) ||
                "An error occurred.";
            setError(detail);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        setUploading(true);
        try {
            if (isEditing && reportData) {
                const newIds = [];
                for (const file of files) {
                    const r = await ReportService.uploadDocument(file);
                    newIds.push(r.data.id);
                }
                const existing = (reportData.attachments_detail || []).map(
                    (d) => d.id,
                );
                await ReportService.updateIncidentReport(reportData.id, {
                    attachments: [...existing, ...newIds],
                });
                const r2 = await ReportService.getIncidentReport(reportData.id);
                setReportData(r2.data);
                onSaved();
            } else {
                for (const file of files) {
                    const objectUrl = URL.createObjectURL(file);
                    const r = await ReportService.uploadDocument(file);
                    setPendingAttachments((prev) => [
                        ...prev,
                        { id: r.data.id, title: file.name, objectUrl },
                    ]);
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const handleDeleteAttachment = async (docId) => {
        if (!isEditing) {
            setPendingAttachments((prev) => {
                const found = prev.find((a) => a.id === docId);
                if (found?.objectUrl) URL.revokeObjectURL(found.objectUrl);
                return prev.filter((a) => a.id !== docId);
            });
            try {
                await ReportService.deleteDocument(docId);
            } catch (err) {
                console.error(err);
            }
            return;
        }
        if (!reportData) return;
        try {
            const ids = (reportData.attachments_detail || [])
                .filter((d) => d.id !== docId)
                .map((d) => d.id);
            await ReportService.updateIncidentReport(reportData.id, {
                attachments: ids,
            });
            await ReportService.deleteDocument(docId);
            const r = await ReportService.getIncidentReport(reportData.id);
            setReportData(r.data);
            onSaved();
        } catch (err) {
            console.error(err);
        }
    };

    const documents = isEditing
        ? reportData?.attachments_detail || []
        : pendingAttachments;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            fullScreen={fullScreen}
        >
            <DialogTitle>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    {isEditing ? "Edit Incident Report" : "New Incident Report"}
                    {fullScreen && (
                        <IconButton size="small" onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    {error && (
                        <Grid item xs={12}>
                            <Alert severity="error">{error}</Alert>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                            Link to (optional)
                        </Typography>
                        <ToggleButtonGroup
                            value={form.linkedTo}
                            exclusive
                            onChange={handleLinkedToChange}
                            size="small"
                            fullWidth
                            sx={{ display: "flex", mt: 0.5 }}
                        >
                            <ToggleButton value="project" sx={{ flex: 1 }}>
                                Project
                            </ToggleButton>
                            <ToggleButton value="quote" sx={{ flex: 1 }}>
                                Quote
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>

                    <Grid item xs={12}>
                        {form.linkedTo === "project" ? (
                            <ProjectPicker
                                value={form.project}
                                onChange={set("project")}
                                label="Project"
                            />
                        ) : (
                            <QuotePicker
                                value={form.quote}
                                onChange={set("quote")}
                                label="Quote"
                            />
                        )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Report Date"
                            type="date"
                            value={form.report_date}
                            onChange={(e) => set("report_date")(e.target.value)}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: today }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            label="Category"
                            value={form.category}
                            onChange={(e) => set("category")(e.target.value)}
                            fullWidth
                        >
                            <MenuItem value="">
                                <em>— None —</em>
                            </MenuItem>
                            {CATEGORY_CHOICES.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                    {opt}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Location"
                            value={form.location}
                            onChange={(e) => set("location")(e.target.value)}
                            fullWidth
                            inputProps={{ maxLength: 300 }}
                            helperText={`${form.location.length}/300`}
                            FormHelperTextProps={{ sx: { textAlign: "right" } }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Witnesses"
                            value={form.witnesses}
                            onChange={(e) => set("witnesses")(e.target.value)}
                            multiline
                            minRows={2}
                            fullWidth
                            inputProps={{ maxLength: 300 }}
                            helperText={`${form.witnesses.length}/300`}
                            FormHelperTextProps={{ sx: { textAlign: "right" } }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Participants"
                            value={form.participants}
                            onChange={(e) => set("participants")(e.target.value)}
                            multiline
                            minRows={2}
                            fullWidth
                            inputProps={{ maxLength: 300 }}
                            helperText={`${form.participants.length}/300`}
                            FormHelperTextProps={{ sx: { textAlign: "right" } }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Comments"
                            value={form.comments}
                            onChange={(e) => set("comments")(e.target.value)}
                            multiline
                            minRows={3}
                            fullWidth
                            inputProps={{ maxLength: 2500 }}
                            helperText={`${form.comments.length}/2500`}
                            FormHelperTextProps={{ sx: { textAlign: "right" } }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                mb={1}
                            >
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                >
                                    Attachments
                                    {documents.length > 0
                                        ? ` (${documents.length})`
                                        : ""}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={
                                        uploading ? (
                                            <CircularProgress size={12} />
                                        ) : (
                                            <AttachFileIcon />
                                        )
                                    }
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploading}
                                >
                                    {uploading ? "Uploading…" : "Upload"}
                                </Button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*,application/pdf"
                                    multiple
                                    hidden
                                    onChange={handleFileChange}
                                />
                            </Stack>

                            {documents.length > 0 ? (
                                <List dense disablePadding>
                                    {documents.map((doc, idx) => (
                                        <ListItem
                                            key={doc.id}
                                            disableGutters
                                            divider={idx < documents.length - 1}
                                            secondaryAction={
                                                <Stack direction="row" spacing={0.5}>
                                                    {(doc.document_url ||
                                                        doc.objectUrl) && (
                                                        <Tooltip title="Open">
                                                            <IconButton
                                                                size="small"
                                                                component="a"
                                                                href={
                                                                    doc.document_url ||
                                                                    doc.objectUrl
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                <OpenInNewIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title="Remove">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() =>
                                                                handleDeleteAttachment(
                                                                    doc.id,
                                                                )
                                                            }
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            }
                                        >
                                            <ListItemText
                                                primary={doc.title}
                                                primaryTypographyProps={{
                                                    variant: "body2",
                                                    noWrap: true,
                                                    sx: { pr: 9 },
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {isEditing
                                        ? "No attachments yet."
                                        : "Photos can be attached after saving too."}
                                </Typography>
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions
                sx={
                    fullScreen
                        ? { pb: "max(8px, env(safe-area-inset-bottom))" }
                        : undefined
                }
            >
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                >
                    {isEditing ? "Save Changes" : "Create Report"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
